import { Box } from '@material-ui/core';
import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { pure } from 'recompose';
import { useQuery, useApolloClient, DocumentNode } from '@apollo/client';
import queryString from 'query-string';

import Row from './Row';
import List from './List';
import PaginationControls from './PaginationControls';
import EntityLoadingState from '../entity-states/EntityLoadingState';
import EntityErrorState from '../entity-states/EntityErrorState';
import EntityEmptyState from '../entity-states/EntityEmptyState';

import { isArray, isDefinedAndNotNull } from '../../utilities/check';

const State = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

// Dummy query to prevent useQuery from crashing when skipped (legacy mode)
import { gql } from '@apollo/client';
const DUMMY_QUERY = gql`
  query Dummy {
    __typename
  }
`;

const asState = (Component: any) => {
  const StateComponent = (props: any) => (
    <State>
      <Component {...props} />
    </State>
  );
  StateComponent.displayName = `StateWrapper(${Component.displayName || Component.name || 'Component'})`;
  return StateComponent;
};

const StateWrapper = ({ data, getList, Loading, Error, Empty, children }: any) => {
  const isLoading = !!data.loading;
  const hasError = !!data.error;
  const list = getList(data);
  const isEmpty = !isArray(list) || !list.length;

  const ErrorState = asState(Error);
  const LoadingState = asState(Loading);
  const EmptyState = asState(Empty);

  if (isLoading && isEmpty) {
    return <LoadingState />;
  } else if (hasError) {
    return <ErrorState error={data.error} />;
  } else if (isEmpty) {
    return (
      <Box flex={1} py={12}>
        <EmptyState />
      </Box>
    );
  } else {
    return children;
  }
};

// ============================================================================
// Logic Utilities
// ============================================================================

const mapFilter = (filter: any) => {
  if (isDefinedAndNotNull(filter)) {
    const { dateRange, physicians, ...rest } = filter;
    return {
      dateRange: dateRange && dateRange.toJSON ? dateRange.toJSON() : dateRange,
      physicians: physicians ? physicians.map((id: any) => Number(id)) : undefined,
      ...rest,
    };
  } else {
    return filter;
  }
};

const buildFilterVariables = (filter: any, pickFilter?: (filter: any) => any) => {
  const mappedFilter = mapFilter(filter);
  const filterPayload = pickFilter ? pickFilter(mappedFilter) : mappedFilter;

  return {
    ...filterPayload,
    filter: filterPayload,
  };
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * Default page size for all paginated lists.
 * Can be overridden per-list via paginationConfig.pageSize
 */
export const DEFAULT_PAGE_SIZE = 50;

export interface PaginationConfig {
  query: DocumentNode;
  countQuery?: DocumentNode;
  /**
   * Subscription document for IDs-based real-time updates.
   * Should accept `procedureIds: [Long!]!` variable.
   */
  subscription?: DocumentNode;
  /**
   * Key in subscription response (e.g., 'proceduresById').
   * Defaults to 'proceduresById' if not specified.
   */
  subscriptionKey?: string;
  countKey?: string;
  entityKey?: string;
  /**
   * Number of items per page. Defaults to DEFAULT_PAGE_SIZE (50).
   */
  pageSize?: number;
}

export interface CollectionListOnlyProps {
  match?: any;
  location?: any;
  history?: any;
  data?: any;
  getList: (data: any) => any;
  columns?: any[];
  defaultDisplayColumns?: any[];
  hideColumns?: any[];
  View?: any;
  onViewClick?: any;
  showUrlProvider?: any;
  idProvider?: any;
  Loading?: any;
  Error?: any;
  Empty?: any;
  FilterComponent?: any;
  FooterComponent?: any;
  filter?: any;
  setFilter?: any;
  highlightedProvider?: any;
  highlightedRowStyles?: any;
  highlightedRowStyleProvider?: any;
  AdditionalBlock?: any;
  containsSeparator?: any;
  useColumnSelection?: any;
  useCSVExport?: any;
  MobileItemComponent?: any;
  tableKey?: string;
  simpleFilter?: boolean;
  defaultSort?: any;
  searchSource?: any;
  customExportCSV?: any;
  customExportXLSX?: any;
  customExportPDF?: any;
  exportFileName?: string;
  // Pagination props
  paginationConfig?: PaginationConfig;
  paginationState?: { page: number; offset?: number; limit?: number };
  onPageChange?: (state: any) => void;
  totalCount?: number;
  pickFilter?: (filter: any) => any;
  currentSort?: { title?: string; type?: 'asc' | 'desc' };
  onSortChange?: (sort: any) => void;
  // Custom content for left side of list header (e.g., stat chips)
  // Can be a ReactNode or a function that receives { filter } and returns ReactNode
  HeaderLeft?: React.ReactNode | ((props: { filter?: any }) => React.ReactNode);
}

const CollectionListOnly: React.FC<CollectionListOnlyProps> = ({
  match: _match,
  location,
  history,
  data,
  getList,
  columns,
  defaultDisplayColumns,
  hideColumns,
  View = Row,
  onViewClick,
  showUrlProvider,
  idProvider,
  Loading = EntityLoadingState,
  Error = EntityErrorState,
  Empty = EntityEmptyState,
  FilterComponent,
  FooterComponent,
  filter,
  setFilter,
  highlightedProvider,
  highlightedRowStyles,
  highlightedRowStyleProvider,
  AdditionalBlock,
  containsSeparator,
  useColumnSelection,
  useCSVExport,
  MobileItemComponent,
  tableKey,
  simpleFilter,
  defaultSort,
  searchSource,
  customExportCSV,
  customExportXLSX,
  customExportPDF,
  exportFileName,
  // Pagination props
  paginationConfig,
  paginationState: externalPaginationState,
  onPageChange: externalOnPageChange,
  totalCount: externalTotalCount,
  pickFilter, // Passed from CollectionList for variable mapping
  currentSort: externalCurrentSort,
  onSortChange: externalOnSortChange,
  HeaderLeft,
}) => {
  // ========================================================================
  // Pagination state management with URL sync
  // ========================================================================
  const isPaginated = !!paginationConfig;
  const pageSize = paginationConfig?.pageSize || DEFAULT_PAGE_SIZE;
  const client = useApolloClient(); // For IDs-based subscription

  // Parse initial page from URL (human-readable: 1-indexed in URL, 0-indexed internally)
  const getInitialPageFromUrl = () => {
    if (!location?.search) return 0;
    const params = queryString.parse(location.search);
    // URL uses human-readable pages (1, 2, 3...), internal state uses 0-indexed
    const urlPage = params.page ? parseInt(params.page as string, 10) : 1;
    return isNaN(urlPage) || urlPage < 1 ? 0 : urlPage - 1;
  };

  // Parse initial sort from URL
  const getInitialSortFromUrl = () => {
    if (!location?.search) return defaultSort || {};
    const params = queryString.parse(location.search);
    if (params.sortBy) {
      return {
        title: params.sortBy as string,
        type: (params.sortOrder as 'asc' | 'desc') || 'asc',
      };
    }
    return defaultSort || {};
  };

  // Internal state - initialized from URL on first render only
  const [internalPage, setInternalPage] = useState(getInitialPageFromUrl);
  const [internalSort, setInternalSort] = useState(getInitialSortFromUrl);

  // Use external state if provided, otherwise internal
  const currentPage = externalPaginationState?.page !== undefined ? externalPaginationState.page : internalPage;

  // Update URL when page/sort changes (called only on user action, not on every render)
  // IMPORTANT: Use window.location.search so we always see the latest filter params
  // written by withFilterQueryString (which also uses window.history.replaceState).
  const updateUrlWithPagination = useCallback(
    (page: number, sort?: { title?: string; type?: string }) => {
      const currentParams = queryString.parse(window.location.search);
      // URL uses human-readable pages (1, 2, 3...), internal state uses 0-indexed
      const humanPage = page + 1;
      const newParams = {
        ...currentParams,
        page: humanPage > 1 ? String(humanPage) : undefined, // Don't include page=1 in URL (it's the default)
        sortBy: sort?.title || currentParams.sortBy,
        sortOrder: sort?.type || currentParams.sortOrder,
      };

      // Remove undefined/null values
      Object.keys(newParams).forEach(key => {
        if (newParams[key] === undefined || newParams[key] === null) {
          delete newParams[key];
        }
      });

      const search = queryString.stringify(newParams);
      const basePath = location?.pathname || window.location.pathname;
      const newUrl = basePath + (search ? `?${search}` : '');

      // Use replaceState to avoid adding to history stack for pagination
      window.history.replaceState(null, '', newUrl);
    },
    [location]
  );

  // ========================================================================
  // Data Fetching via Hooks (Modern React)
  // ========================================================================

  // 1. Fetch List Data (if paginated)
  const listVariables = useMemo(() => {
    if (!isPaginated) return {};

    // Resolve sort key from columns
    const effectiveSort = externalCurrentSort || internalSort;
    const sortColumn = columns?.find((c: any) => c.title === effectiveSort.title);
    const sortKey = sortColumn?.sortKey;

    return {
      ...buildFilterVariables(filter, pickFilter),
      offset: currentPage * pageSize,
      limit: pageSize,
      sortBy: sortKey,
      // NOTE: GraphQL query expects `sortOrder` variable name
      sortOrder: effectiveSort.type,
    };
  }, [isPaginated, filter, pickFilter, currentPage, pageSize, internalSort, externalCurrentSort, columns]);

  const handleSort = useCallback(
    (newSort: any) => {
      if (externalOnSortChange) {
        externalOnSortChange(newSort);
      } else {
        setInternalSort(newSort);
        setInternalPage(0); // Reset to first page on sort change
        // Sync to URL for back button support
        updateUrlWithPagination(0, newSort);
      }
    },
    [externalOnSortChange, updateUrlWithPagination]
  );

  const {
    data: listData,
    loading: listLoading,
    error: listError,
    networkStatus: listNetworkStatus,
  } = useQuery(paginationConfig?.query || DUMMY_QUERY, {
    skip: !isPaginated,
    variables: listVariables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true, // Crucial for loading state on page change
  });

  // IDs-based subscription for efficient real-time updates
  // Only subscribes to items on current page (not full filtered list)
  const pageItemIds = useMemo(() => {
    const items = isPaginated ? listData?.[paginationConfig?.entityKey || ''] || [] : [];
    return items.map((item: any) => item?.id).filter((id: number) => id != null);
  }, [isPaginated, listData, paginationConfig?.entityKey]);

  // Stable string for subscription dependency
  const pageItemIdsKey = pageItemIds.join(',');

  // Subscribe to current page items via IDs-based subscription
  useEffect(() => {
    if (!isPaginated || !paginationConfig?.subscription || pageItemIds.length === 0) {
      return;
    }

    // Use Apollo client directly for IDs-based subscription
    const observable = client.subscribe({
      query: paginationConfig.subscription,
      variables: { procedureIds: pageItemIds },
    });

    const subscription = observable.subscribe({
      next: result => {
        const subscriptionKey = paginationConfig.subscriptionKey || 'proceduresById';
        const updatedItems = result.data?.[subscriptionKey] as any[] | undefined;

        if (!updatedItems || updatedItems.length === 0) return;

        // Update each item in the cache by merging with existing data
        // This ensures Apollo broadcasts the update to all watching queries
        updatedItems.forEach((item: any) => {
          if (!item?.id || !item?.__typename) return;

          const cacheId = client.cache.identify(item);
          if (!cacheId) return;

          // Merge the updated item into the cache with broadcast enabled
          // This updates all fields from the subscription response
          client.cache.modify({
            id: cacheId,
            fields: Object.fromEntries(
              Object.entries(item)
                .filter(([key]) => key !== '__typename' && key !== 'id')
                .map(([key, value]) => [key, () => value])
            ),
            broadcast: true,
          });
        });
      },
      error: () => {
        // Subscription error - will retry on reconnect
      },
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginated, paginationConfig, pageItemIdsKey, client, listVariables]);

  // 2. Fetch Count Data (if needed)
  const shouldFetchCount = isPaginated && paginationConfig?.countQuery && externalTotalCount === undefined;

  // Count variables typically just need filter, no offset/limit
  const countVariables = useMemo(() => {
    if (!shouldFetchCount) return {};
    return buildFilterVariables(filter, pickFilter);
  }, [shouldFetchCount, filter, pickFilter]);

  const { data: countData, loading: countLoading } = useQuery(
    paginationConfig?.countQuery || paginationConfig?.query || DUMMY_QUERY,
    {
      skip: !shouldFetchCount,
      variables: countVariables,
      fetchPolicy: 'cache-and-network',
    }
  );

  // ========================================================================
  // Data Consolidation
  // ========================================================================

  // Determine actual totalCount
  const countKey = paginationConfig?.countKey;
  const internalTotalCount = countKey && countData ? countData[countKey] : 0;
  const totalCount = externalTotalCount !== undefined ? externalTotalCount : internalTotalCount;

  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 0;

  // Combine data for display
  const activeData = isPaginated
    ? {
        ...listData,
        loading: listLoading, // Wait for list loading (count is separate)
        error: listError,
        networkStatus: listNetworkStatus,
      }
    : data;

  // ========================================================================
  // Event Handlers
  // ========================================================================

  // Reset to first page when filter changes (but don't update URL - filter already updates URL via withFilterQueryString)
  const filterString = JSON.stringify(filter);
  const prevFilterRef = React.useRef(filterString);
  useEffect(() => {
    // Only reset page if filter actually changed (not on initial mount)
    if (prevFilterRef.current !== filterString) {
      prevFilterRef.current = filterString;
      setInternalPage(0);
      // Update URL to remove page param when filter changes.
      // Read from window.location.search to keep in sync with withFilterQueryString.
      if (isPaginated && !externalPaginationState) {
        const currentParams = queryString.parse(window.location.search);
        if (currentParams.page) {
          delete currentParams.page;
          const search = queryString.stringify(currentParams);
          const basePath = location?.pathname || window.location.pathname;
          const newUrl = basePath + (search ? `?${search}` : '');
          window.history.replaceState(null, '', newUrl);
        }
      }
    }
  }, [filterString, isPaginated, externalPaginationState, location]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (externalOnPageChange) {
        externalOnPageChange({ offset: newPage * pageSize, limit: pageSize, page: newPage });
      } else {
        setInternalPage(newPage);
        // Sync to URL for back button support
        updateUrlWithPagination(newPage, internalSort);
      }
    },
    [externalOnPageChange, pageSize, updateUrlWithPagination, internalSort]
  );

  // Sort visible columns according to storage order (handled in List, but we need to pass safe columns)
  // Disable sorting for columns that don't have a sortKey when in pagination mode
  const displayColumns = useMemo(() => {
    if (!columns) return [];
    if (!isPaginated) return columns;
    return columns.map((col: any) => ({
      ...col,
      sortDisabled: col.sortDisabled || !col.sortKey,
    }));
  }, [columns, isPaginated]);

  return (
    <Fragment>
      {FilterComponent && setFilter && (
        <FilterComponent onChange={setFilter} value={filter} searchSource={searchSource} />
      )}

      <StateWrapper Loading={Loading} Error={Error} Empty={Empty} getList={getList} data={activeData}>
        <Fragment>
          {AdditionalBlock && <AdditionalBlock filter={filter} />}
          <List
            Component={View}
            getList={getList}
            data={activeData}
            history={history}
            location={location}
            hideColumns={hideColumns as any}
            columns={displayColumns}
            defaultDisplayColumns={defaultDisplayColumns}
            showUrlProvider={showUrlProvider}
            idProvider={idProvider}
            onViewClick={onViewClick}
            highlightedProvider={highlightedProvider}
            highlightedRowStyles={highlightedRowStyles}
            highlightedRowStyleProvider={highlightedRowStyleProvider}
            containsSeparator={!!containsSeparator}
            tableKey={tableKey || 'default'}
            defaultSort={defaultSort}
            useColumnSelection={useColumnSelection}
            useCSVExport={useCSVExport}
            MobileItemComponent={MobileItemComponent}
            simpleFilter={!(FilterComponent && setFilter) && simpleFilter}
            customExportCSV={customExportCSV}
            customExportXLSX={customExportXLSX}
            customExportPDF={customExportPDF}
            exportFileName={exportFileName}
            // Controlled sort
            onSort={isPaginated ? handleSort : undefined}
            currentSort={isPaginated ? externalCurrentSort || internalSort : undefined}
            HeaderLeft={typeof HeaderLeft === 'function' ? HeaderLeft({ filter }) : HeaderLeft}
          />
        </Fragment>
      </StateWrapper>

      {/* Pagination Controls - show if paginated, even if count is 0 while loading */}
      {isPaginated && (totalCount > 0 || countLoading) && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          loading={listLoading || countLoading}
        />
      )}

      {FooterComponent && <FooterComponent />}
    </Fragment>
  );
};

(CollectionListOnly as any).propTypes = {
  getList: PropTypes.func.isRequired,
  data: PropTypes.object,
  columns: PropTypes.array.isRequired,
  paginationConfig: PropTypes.shape({
    query: PropTypes.object,
    countQuery: PropTypes.object,
    countKey: PropTypes.string,
    entityKey: PropTypes.string,
    pageSize: PropTypes.number,
  }),
  pickFilter: PropTypes.func,
};

export default pure(CollectionListOnly);
